import * as z from "zod/mini";

/**
 * Model selection's wire, parsed at runtime.
 *
 * Model selection does not use the console's exception from runtime parsing:
 * assistant-api's `setModel` accepts any resolved user (ENR-4398), so the
 * first control the exception rests on does not hold for it, and the rule's
 * revisit trigger applies. Each schema mirrors assistant-api's generated type
 * for the same body, and `model-selection-wire.test.ts` holds them equal.
 * See `apps/ops-console/docs/service-contracts.md`.
 */

/* The console's CSP has no `unsafe-eval`, which zod's compiled fast path
   needs; without this it tries, is refused, and falls back. */
z.config({ jitless: true });

const ModelId = z.enum([
  "anthropic.claude-sonnet-4-20250514-v1:0",
  "moonshotai.kimi-k2.5",
  "anthropic.claude-haiku-4-20250514-v1:0",
]);

const Meta = z.object({ request_id: z.string() });

export const SetModelBody = z.object({ modelId: z.string().check(z.minLength(1)) });

export const ModelSelectionResponse = z.object({
  data: z.object({
    currentModelId: z.string(),
    availableModels: z.array(z.object({ id: ModelId, name: z.string(), provider: z.string() })),
  }),
  meta: Meta,
});

export const ModelUpdateResponse = z.object({
  data: z.object({
    modelId: ModelId,
    message: z.literal("Model updated. Change takes effect on next Lambda cold start."),
  }),
  meta: Meta,
});

/** A body that does not match its schema is refused here, before it is used or sent. */
export function parseWire<Schema extends z.ZodMiniType>(
  schema: Schema,
  value: unknown,
  what: string
): z.infer<Schema> {
  const result = z.safeParse(schema, value);
  if (!result.success) {
    throw new Error(
      `${what} does not match assistant-api's contract: ${z.prettifyError(result.error)}`
    );
  }
  return result.data;
}
